import React, { useEffect, useRef, useState } from 'react'
import {
  CCol, CRow, CAlert, CForm, CFormInput, CFormLabel, CFormSelect, CButton, CSpinner,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CCard, CCardHeader, CCardBody,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilUser, cilLockLocked } from '@coreui/icons'
import { api, currentUser } from '../api/client'
import WwGrid, { WwColumn } from '../components/ww/WwGrid'
import { usePendiente } from '../components/ww/pendiente'

const columnas: WwColumn[] = [
  // La grilla NO muestra campos técnicos (GAMID, EmpresaCodigo, RolCodigo):
  // solo conceptos humanos (decisión Rafael 2026-08-27).
  { key: 'usuarioCodigo', title: 'Nombre de usuario' },
  { key: 'usuarioEmail', title: 'Email' },
  { key: 'empresaNombre', title: 'Empresa' },
  { key: 'usuarioNombre', title: 'Nombre' },
  { key: 'usuarioEstado', title: 'Estado' },
  { key: 'rolNombre', title: 'Rol' },
  { key: 'usuarioOnline', title: 'Clásica', type: 'bool' },
]

// Usuarios — réplica de usuariogeneral.cs:53 (usuariogeneral GX).
const UsuariosPage: React.FC = () => {
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [rows, setRows] = useState<any[]>([])
  const formRef = useRef<HTMLFormElement>(null)
  // Catálogo de roles del sistema (GET /api/roles — RolesController:73-81):
  // el select muestra NOMBRES, el backend recibe el RolCodigo.
  // El administrador de EMPRESA asigna Administrador/Cobrador/SUPERVISOR
  // (el rol 4 faltaba — pedido Rafael 2026-09-13); el superadmin ve TODOS
  // los roles (decisión Rafael 2026-08-27).
  const [roles, setRoles] = useState<{ rolCodigo: number; rolNombre: string }[]>([])
  useEffect(() => {
    api.get<any[]>('/roles').then((todos) => {
      const esSuperadmin = currentUser()?.role === 'superadmin'
      setRoles(esSuperadmin ? todos : todos.filter((r) => r.rolCodigo === 1 || r.rolCodigo === 2 || r.rolCodigo === 4))
    }).catch(() => undefined)
  }, [])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const { aviso, setAviso } = usePendiente()
  const [abierto, setAbierto] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [errorModal, setErrorModal] = useState('')
  const [editando, setEditando] = useState<any | null>(null)
  const [form, setForm] = useState({
    empresaCodigo: empresa, usuarioCodigo: '', gamId: '',
    email: '', nombre: '', clave: '', estado: 'Activo', rolCodigo: '',
  })

  useEffect(() => {
    api.get<any[]>(`/usuarios?empresa=${empresa}`)
      .then(setRows)
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
  }, [empresa])

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorModal('')
    // [RGM] 2026-10-09 (pedido Rafael — hueco de seguridad confirmado): el
    // ALTA exige clave 6–15, MISMA regla del modal "Cambiar Clave"
    // (SuperAdminUsuariosPage.guardarClave). En EDICIÓN se mantiene
    // "vacío = conservar la clave actual" (el backend no la toca).
    if (!editando) {
      const clave = form.clave.trim()
      if (clave.length < 6) {
        setErrorModal('La clave debe tener al menos 6 caracteres')
        return
      }
      if (clave.length > 15) {
        setErrorModal('La clave no puede tener más de 15 caracteres')
        return
      }
    }
    setGuardando(true)
    try {
      if (editando) {
        // usuarioww.cs:2588-2600 — DO UPDATE: webobject usuario Mode="UPD" con
        // UsuarioCodigo (la PK viaja en la URL, no cambia). La clave vacía
        // no se modifica en el backend.
        await api.put(`/usuarios/${editando.usuarioCodigo}`, {
          empresaCodigo: form.empresaCodigo,
          email: form.email,
          nombre: form.nombre,
          clave: form.clave || undefined,
          estado: form.estado,
          rolCodigo: Number(form.rolCodigo),
        })
      } else {
        await api.post('/usuarios', {
          empresaCodigo: form.empresaCodigo,
          usuarioCodigo: form.usuarioCodigo,
          gamId: form.gamId || undefined,
          email: form.email,
          nombre: form.nombre,
          clave: form.clave,
          estado: form.estado,
          rolCodigo: Number(form.rolCodigo),
        })
      }
      setAbierto(false)
      setEditando(null)
      api.get<any[]>(`/usuarios?empresa=${empresa}`).then(setRows)
    } catch (err: any) {
      setErrorModal(err.message)
    } finally {
      setGuardando(false)
    }
  }

  // usuarioww.cs:2588-2600 — Modificar: carga la fila en el form y abre el modal.
  const onEditar = (row: any) => {
    setErrorModal('')
    setEditando(row)
    setForm({
      empresaCodigo: empresa,
      usuarioCodigo: row.usuarioCodigo ?? '',
      gamId: row.usuarioGAMID ?? '',
      email: row.usuarioEmail ?? '',
      nombre: row.usuarioNombre ?? '',
      clave: '',
      estado: row.usuarioEstado ?? '',
      rolCodigo: String(row.rolCodigo ?? ''),
    })
    setAbierto(true)
  }

  const eliminar = (row: any) => {
    // usuarioww.cs:2604-2616 — DO DELETE: webobject usuario Mode="DLT" con
    // UsuarioCodigo. El backend replica el control de referencias de
    // usuario_bc.cs:547-581 (Supervisor/Cobrador).
    if (!window.confirm(`¿Eliminar el usuario ${row.usuarioCodigo}?`)) return
    api.delete(`/usuarios/${row.usuarioCodigo}?empresa=${empresa}`)
      .then(() => api.get<any[]>(`/usuarios?empresa=${empresa}`).then(setRows))
      .catch((e: any) => setError(e.message))
  }

  const [totalReg, setTotalReg] = useState(rows.length)
  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4>Usuario</h4>
          <small className="text-body-secondary">{totalReg} {totalReg === 1 ? 'registro' : 'registros'}</small>
        </CCol>
      </CRow>
      {error && <CAlert color="danger">{error}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}

      <WwGrid
        title="Usuario"
        columns={columnas}
        rows={rows}
        onTotalChange={setTotalReg}
        loading={cargando}
        // Crear usuario: el form SIEMPRE arranca VACÍO (antes quedaba con los
        // datos del último usuario editado — bug reportado Rafael 2026-08-27).
        onAgregar={() => {
          setEditando(null)
          setErrorModal('')
          setForm({ empresaCodigo: empresa, usuarioCodigo: '', gamId: '', email: '', nombre: '', clave: '', estado: 'Activo', rolCodigo: '' })
          setAbierto(true)
        }}
        onEditar={onEditar}
        onEliminar={eliminar}
      />

      <CModal visible={abierto} onClose={() => { setAbierto(false); setEditando(null) }}>
        <CModalHeader>
          <CModalTitle>{editando ? 'Modificar usuario' : 'Agregar usuario'}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {errorModal && <CAlert color="danger">{errorModal}</CAlert>}
          <CForm ref={formRef} onSubmit={guardar}>
            {/* ── TARJETA 1 — DATOS DEL USUARIO ── */}
            <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
              <CCardHeader className="d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
                <CIcon icon={cilUser} size="sm" /> Datos del Usuario
              </CCardHeader>
              <CCardBody style={{ padding: '14px 16px' }}>
                <CRow className="g-3">
                  <CCol md={6}>
                    <CFormLabel>Nombre</CFormLabel>
                    {/* Nombre de pila: MAYÚSCULA (patrón GX, decisión vigente).
                        El "Nombre de usuario" (login) va tal cual se escribe. */}
                    <CFormInput value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value.toUpperCase() })} />
                  </CCol>
                  <CCol md={6}>
                    {/* "Código" NO — es el nombre de usuario/apodo (decisión Rafael) */}
                    <CFormLabel>Nombre de usuario</CFormLabel>
                    <CFormInput value={form.usuarioCodigo} disabled={editando !== null} onChange={(e) => setForm({ ...form, usuarioCodigo: e.target.value })} />
                  </CCol>
                  <CCol md={12}>
                    <CFormLabel>Email</CFormLabel>
                    <CFormInput type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>

            {/* ── TARJETA 2 — ACCESO Y PERMISOS ── */}
            <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
              <CCardHeader className="d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
                <CIcon icon={cilLockLocked} size="sm" /> Acceso y Permisos
              </CCardHeader>
              <CCardBody style={{ padding: '14px 16px' }}>
                <CRow className="g-3">
                  <CCol md={6}>
                    <CFormLabel>Rol</CFormLabel>
                    {/* Select de NOMBRES (catálogo /api/roles); el backend recibe RolCodigo */}
                    <CFormSelect value={form.rolCodigo} onChange={(e) => setForm({ ...form, rolCodigo: e.target.value })}>
                      <option value="">Seleccione...</option>
                      {roles.map((r) => <option key={r.rolCodigo} value={r.rolCodigo}>{r.rolNombre}</option>)}
                    </CFormSelect>
                  </CCol>
                  <CCol md={6}>
                    <CFormLabel>Estado</CFormLabel>
                    <CFormSelect value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })}>
                      <option value="">Seleccione...</option>
                      <option value="Activo">Activo</option>
                      <option value="Inactivo">Inactivo</option>
                    </CFormSelect>
                  </CCol>
                  <CCol md={12}>
                    <CFormLabel>{editando ? 'Nueva clave' : 'Clave'}</CFormLabel>
                    {/* Clave 6–15 (char(15) GX): mismo límite del modal
                        "Cambiar Clave" — el ALTA la exige (ver guardar()). */}
                    <CFormInput type="password" value={form.clave} maxLength={15} onChange={(e) => setForm({ ...form, clave: e.target.value })} placeholder={editando ? 'Déjala vacía para conservar la actual' : ''} />
                    {editando && <small className="text-body-secondary">Déjala vacía para conservar la clave actual.</small>}
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>

          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" disabled={guardando} onClick={() => { setAbierto(false); setEditando(null) }}>Cancelar</CButton>
          <CButton color="primary" type="button" disabled={guardando} onClick={() => formRef.current?.requestSubmit()}>{guardando ? <CSpinner size="sm" /> : editando ? 'Guardar cambios' : 'Crear usuario'}</CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default UsuariosPage
